import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { InputText } from 'app/components/elements/InputText';
import React, { ChangeEvent, useState } from 'react';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { useInjection } from './hook';
import { InputSelect } from 'app/components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { CATEGORY } from 'types/documents';
import { replace } from 'lodash';
import { ModalAddDocument } from './components/ModalCreateDocument';
import { ModalManageDocumentationCategories } from './components/ModalManageDocumentationCategories';
import { CustomPagination } from 'app/components/elements/Pagination';
import { saveVideoFile } from 'utils/downloadFile';
import { Image } from '../../components/elements/Image';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';

const openDocumentationInNewTab = (url: string) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const DocumentsPage = () => {
  const {
    valueSearch,
    setValueSearch,
    valueFilterClient,
    handleSearch,
    filterByTypeClient,
    pagingInfo,
    handleChangePage,
    documents,
    handleChangeCategory,
    categoryActive,
    loading,
    optionClient,
    libraryCategories,
    reloadLibraryCategories,
    optionCategory,
    showModalAddDocument,
    setShowModalAddDocument,
    asyncAddDocument,
    handleDeleteDocumentation
  } = useInjection();
  const { auth } = useAuth();
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);

  const isAssetsOrModelsTab =
    categoryActive === CATEGORY.ASSETS || categoryActive === CATEGORY.MODELS;

  return (
    <WrapperPage>
      <TitlePage>Documentation</TitlePage>
      <Stack
        width={'100%'}
        style={{
          marginBottom: '20px',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
        <Stack
          flexDirection="row"
          flexWrap="wrap"
          alignItems="center"
          gap={1}
          sx={{ rowGap: 1 }}
          flex={1}>
          {libraryCategories.map((cat) => (
            <Button key={cat.id} onClick={() => handleChangeCategory(cat.slug)}>
              <Typography
                sx={{ cursor: 'pointer' }}
                fontSize={'18px'}
                fontWeight={cat.slug === categoryActive ? '900' : '400'}
                color={cat.slug === categoryActive ? '#226AAA' : '#3E3E3E'}>
                {replace(cat.slug, '_', ' ')}
              </Typography>
            </Button>
          ))}
          {[CATEGORY.ASSETS, CATEGORY.MODELS].map((item) => (
            <Button key={item} onClick={() => handleChangeCategory(item)}>
              <Typography
                sx={{ cursor: 'pointer' }}
                fontSize={'18px'}
                fontWeight={item === categoryActive ? '900' : '400'}
                color={item === categoryActive ? '#226AAA' : '#3E3E3E'}>
                {replace(item, '_', ' ')}
              </Typography>
            </Button>
          ))}
        </Stack>
        {[Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role || '') && (
          <ButtonCustom
            label="Manage categories"
            onClick={() => setManageCategoriesOpen(true)}
            style={{ width: 'max-content', height: 40 }}
          />
        )}
      </Stack>

      <Stack
        width={'100%'}
        flexDirection={'row'}
        alignItems={'flex-start'}
        flexWrap={'wrap'}
        justifyContent={'space-between'}>
        {!isAssetsOrModelsTab && (
          <>
            <Stack flexDirection={'row'} gap={1.25}>
              <Stack gap={1}>
                <InputText
                  startAdornment={
                    <InputAdornment position="end">
                      <IconButton>
                        <img alt={'icon-user'} src={'/icons/icon_search.svg'} width={24} />
                      </IconButton>
                    </InputAdornment>
                  }
                  placeholder={'Search'}
                  inputProps={{
                    value: valueSearch,
                    style: {
                      width: '352px',
                      minHeight: '46px',
                      height: '46px',
                      padding: 0
                    },
                    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      setValueSearch(event.target.value);
                    },
                    onKeyDown: (
                      event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      if (event.key === 'Enter') {
                        handleSearch();
                      }
                    }
                  }}
                />
              </Stack>
              <ButtonCustom
                sx={{ width: 'max-content', height: '46px' }}
                label={'Search'}
                onClick={handleSearch}
              />
            </Stack>
            {[Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role || '') && (
              <>
                <Stack>
                  <InputSelect
                    value={valueFilterClient}
                    placeholder={'Filter by Client'}
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      filterByTypeClient(e.target.value as string);
                    }}
                    sx={{ width: '352px', minHeight: '48px', height: '48px', padding: 0 }}
                    options={optionClient}
                  />
                </Stack>
                <ButtonCustom
                  label={'Add Document'}
                  onClick={() => setShowModalAddDocument(true)}
                  style={{ width: 165, height: 46 }}
                />
              </>
            )}
          </>
        )}
      </Stack>
      {loading ? (
        <CircularProgress color={'primary'} size={'50px'} />
      ) : (
        <Stack alignItems={'flex-end'} gap={'30px'} maxWidth={'1200px'}>
          <Grid
            container
            style={{ marginTop: '20px' }}
            spacing={3}
            justifyContent="flex-start"
            alignItems="center">
            {documents?.map((doc, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={String(doc.id ?? index)}>
                <Stack
                  style={{ width: '100%', height: '100%' }}
                  alignItems={'center'}
                  justifyContent={'center'}
                  gap={'12px'}>
                  <Typography>{doc.name}</Typography>
                  <DownloadContainer
                    style={{ width: '100%', height: '260px', position: 'relative' }}>
                    {[Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role || '') && (
                      <IconButton
                        aria-label="Delete documentation"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDocumentation(doc);
                        }}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 8,
                          zIndex: 4,
                          backgroundColor: '#fff',
                          width: 32,
                          height: 32,
                          minWidth: 32,
                          borderRadius: '16px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                          '&:hover': { backgroundColor: '#fff' }
                        }}>
                        <Typography sx={{ color: '#d32f2f', fontSize: '1.35rem', lineHeight: 1 }}>
                          ×
                        </Typography>
                      </IconButton>
                    )}
                    {doc.storage.type === 'application/pdf' ? (
                      <Stack
                        gap={1}
                        alignItems={'center'}
                        width={'100%'}
                        height={'100%'}
                        onClick={() => openDocumentationInNewTab(doc.url || doc.storage.url)}
                        sx={{ cursor: 'pointer' }}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            saveVideoFile(doc.url || doc.storage.url, doc.storage.fileName);
                          }}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: '#fff',
                            zIndex: 2,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            borderRadius: '50%',
                            padding: 6
                          }}
                          size="small">
                          <img
                            src="/icons/icon_download.svg"
                            alt="download"
                            style={{ width: 24, height: 24 }}
                          />
                        </IconButton>
                        <img
                          style={{
                            height: '100%',
                            width: '100%',
                            objectFit: 'cover'
                          }}
                          src={'/icons/pdf-icon.svg'}
                          alt={'icon'}
                        />
                        <Typography>{doc.storage.fileName}</Typography>
                      </Stack>
                    ) : (
                      <Image
                        disablePreview
                        onClickImage={() => openDocumentationInNewTab(doc.url || doc.storage.url)}
                        onDownLoad={() =>
                          saveVideoFile(doc.url || doc.storage.url, doc.storage.fileName)
                        }
                        url={{ url: doc.url || doc.storage.url, type: doc.storage.type }}
                        styledImage={{ height: '100%', width: '100%', maxHeight: 260 }}
                        styledWrapper={{ width: '100%', height: '100%' }}
                      />
                    )}
                  </DownloadContainer>
                </Stack>
              </Grid>
            ))}
          </Grid>
          <Stack
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent={'center'}
            width={'100%'}
            alignItems={'center'}
            gap={{ xs: 2, md: 'unset' }}
            pt={2.5}>
            {pagingInfo.pageCount !== 0 && (
              <CustomPagination
                page={pagingInfo.currentPage}
                count={pagingInfo.pageCount}
                onChangePage={handleChangePage}
              />
            )}
          </Stack>
        </Stack>
      )}
      <ModalAddDocument
        onCloseModal={() => setShowModalAddDocument(false)}
        isOpen={showModalAddDocument}
        onSubmit={asyncAddDocument}
        optionClient={optionClient}
        category={!isAssetsOrModelsTab ? optionCategory : undefined}
      />
      <ModalManageDocumentationCategories
        open={manageCategoriesOpen}
        onClose={() => setManageCategoriesOpen(false)}
        categories={libraryCategories}
        onReload={reloadLibraryCategories}
      />
    </WrapperPage>
  );
};
const WrapperPage = styled(Stack)(() => ({
  padding: '40px 30px 50px 40px',
  width: '100%',
  minHeight: '100vh'
}));
const TitlePage = styled(Typography)(() => ({
  color: '#226AAA',
  fontSize: '24px',
  fontWeight: 700,
  marginBottom: '20px'
}));
const DownloadContainer = styled(Stack)`
  position: relative;
  display: flex;
  align-items: center;
  ::before {
    content: '\\2193';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 2em;
    color: #333;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;
